import * as React from "react";
import styled from "@emotion/styled";
import {
  easelColors,
  getColorWithOpacity,
  designTokens,
} from "../../helpers/constants";

export type Variant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "link"
  | "destructive";
type ButtonType = "solid" | "outline" | "text";
type Size = "md" | "sm" | "xs" | "stretch";

export interface ButtonStyles {
  backgroundColor?: string;
  color?: string;
  border?: string;
  textDecoration?: "underline" | "none";
}

export interface SizeStyles {
  fontSize?: string;
  padding?: string;
  minWidth?: string;
}

export interface PseudoStyles {
  hover?: ButtonStyles;
  active?: ButtonStyles;
  disabled?: ButtonStyles;
}

export type StyleSet = ButtonStyles & PseudoStyles;

export type StylesByType = Record<ButtonType, StyleSet>;
export type StylesByVariant = Record<Variant, StylesByType>;
export type StylesBySize = Record<Size, SizeStyles>;
export interface EaselButtonProps {
  variant?: Variant;
  buttonType?: ButtonType;
  size?: Size;
  darkBackground?: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  customProps?: StyleSet;
}

export const EaselButton: React.FC<EaselButtonProps> = (
  props: EaselButtonProps
) => {
  return <Button data-testid="easel-button" {...props} />;
};

const variants: StylesByVariant = {
  primary: {
    solid: {
      backgroundColor: easelColors.easelPeriwinkle500,
      color: easelColors.white,
      hover: {
        backgroundColor: easelColors.easelPeriwinkle600,
      },
      active: {
        backgroundColor: easelColors.easelPeriwinkle700,
      },
      disabled: {
        backgroundColor: easelColors.coreGray500,
      },
    },
    outline: {
      backgroundColor: easelColors.white,
      color: easelColors.easelPeriwinkle500,
      border: `1px solid ${easelColors.easelPeriwinkle500}`,
    },
    text: {
      color: easelColors.easelPeriwinkle500,
      backgroundColor: easelColors.white,
      hover: {
        backgroundColor: getColorWithOpacity("almostBlack", 5),
      },
      active: {
        backgroundColor: getColorWithOpacity("almostBlack", 9),
      },
    },
  },
  secondary: {
    solid: {
      backgroundColor: easelColors.almostBlack,
      color: easelColors.white,
      hover: {
        backgroundColor: getColorWithOpacity("almostBlack", 95),
      },
      active: {
        backgroundColor: getColorWithOpacity("almostBlack", 91),
      },
      disabled: {
        // TODO: is this the right color?
        backgroundColor: easelColors.coreGray500,
      },
    },
    outline: {
      backgroundColor: easelColors.white,
      border: `1px solid ${easelColors.almostBlack}`,
      hover: {
        backgroundColor: getColorWithOpacity("almostBlack", 5),
      },
      active: {
        backgroundColor: getColorWithOpacity("almostBlack", 9),
      },
    },
    text: {
      color: easelColors.almostBlack,
      backgroundColor: easelColors.white,
      hover: {
        backgroundColor: getColorWithOpacity("almostBlack", 5),
      },
      active: {
        backgroundColor: getColorWithOpacity("almostBlack", 9),
      },
    },
  },
  tertiary: {
    solid: {},
    text: {},
    outline: {
      backgroundColor: easelColors.white,
      border: `1px solid ${designTokens["ds-color-gray-300"]}`,
      hover: {
        backgroundColor: getColorWithOpacity("almostBlack", 5),
      },
      active: {
        backgroundColor: getColorWithOpacity("almostBlack", 9),
      },
    },
  },
  link: {
    solid: {},
    text: {
      color: easelColors.easelEvergreen500,
      hover: {
        textDecoration: "underline",
      },
    },
    outline: {},
  },
  destructive: {
    solid: {
      backgroundColor: easelColors.easelCoral500,
      color: easelColors.white,
      hover: {
        backgroundColor: easelColors.easelCoral600,
      },
      active: {
        backgroundColor: easelColors.easelCoral700,
      },
      disabled: {
        backgroundColor: easelColors.coreGray500,
      },
    },
    text: {},
    outline: {},
  },
};

// TO DO: update empty styles
const variantsOnDarkBackground: StylesByVariant = {
  primary: {
    solid: {
      backgroundColor: easelColors.easelPeriwinkle400,
      color: easelColors.white,
      hover: {
        backgroundColor: easelColors.easelPeriwinkle500,
      },
      active: {
        backgroundColor: easelColors.easelPeriwinkle600,
      },
      disabled: {
        backgroundColor: easelColors.coreGray500,
      },
    },
    outline: {
      backgroundColor: "transparent",
      color: easelColors.easelPeriwinkle300,
      border: `2px solid ${easelColors.easelPeriwinkle300}`,
      hover: {
        backgroundColor: getColorWithOpacity("white", 5),
      },
      active: {
        backgroundColor: getColorWithOpacity("white", 9),
      },
    },
    text: {},
  },
  secondary: {
    solid: {},
    text: {
      backgroundColor: "transparent",
      color: easelColors.white,
      hover: {
        backgroundColor: getColorWithOpacity("white", 5),
      },
      active: {
        backgroundColor: getColorWithOpacity("white", 9),
      },
    },
    outline: {
      backgroundColor: "transparent",
      border: `2px solid ${easelColors.white}`,
      color: easelColors.white,
      hover: {
        backgroundColor: getColorWithOpacity("white", 5),
      },
      active: {
        backgroundColor: getColorWithOpacity("white", 9),
      },
    },
  },
  tertiary: {
    solid: {},
    text: {},
    outline: {},
  },
  link: {
    solid: {},
    text: {
      color: easelColors.easelEvergreen500,
      hover: {
        textDecoration: "underline",
      },
    },
    outline: {},
  },
  destructive: {
    solid: {},
    text: {},
    outline: {},
  },
};

const sizing: StylesBySize = {
  xs: {
    fontSize: "0.875rem",
    padding: "0.6rem 1rem",
    minWidth: "3.4rem",
  },
  sm: {
    fontSize: "1rem",
    padding: "0.75rem 1.5rem",
    minWidth: "4.68rem",
  },
  md: {
    fontSize: "1rem",
    padding: "1rem 1.5rem",
    minWidth: "5.625rem",
  },
  stretch: {
    fontSize: "1rem",
    minWidth: "100%",
  },
};

const Button = styled.button<EaselButtonProps>(
  ({
    darkBackground = false,
    buttonType = "solid",
    variant = "primary",
    size = "md",
    disabled,
    customProps,
  }: EaselButtonProps) => {
    const variantStyle =
      customProps !== undefined
        ? customProps
        : darkBackground
        ? variantsOnDarkBackground[variant][buttonType]
        : variants[variant][buttonType];

    const sizeStyle = sizing[size];
    return {
      cursor: disabled ? "not-allowed" : "pointer",
      borderRadius: "999px",
      margin: "auto 0",
      lineHeight: "100%",
      "&:focus": {
        outline: "none",
      },
      fontWeight: 500,
      textAlign: "center",
      backgroundColor: variantStyle.backgroundColor || "unset",
      border: variantStyle.border || "none",
      color: variantStyle.color || "unset",
      fontSize: sizeStyle.fontSize || "1rem",
      padding: variant === "link" ? "0.5 0rem" : sizeStyle.padding || "0",
      minWidth: sizeStyle.minWidth || "unset",
      textDecoration: variantStyle.textDecoration || "none",
      "&:hover": {
        backgroundColor:
          variantStyle.hover?.backgroundColor ||
          variantStyle.backgroundColor ||
          "unset",
        border: variantStyle.hover?.border || variantStyle.border || "none",
        color: variantStyle.hover?.color || variantStyle.color || "unset",
        textDecoration:
          variantStyle.hover?.textDecoration ||
          variantStyle.textDecoration ||
          "none",
      },
      "&:active": {
        backgroundColor:
          variantStyle.active?.backgroundColor ||
          variantStyle.backgroundColor ||
          "unset",
        border: variantStyle.active?.border || variantStyle.border || "none",
        color: variantStyle.active?.color || variantStyle.color || "unset",
        textDecoration:
          variantStyle.active?.textDecoration ||
          variantStyle.textDecoration ||
          "none",
      },
      "&:disabled": {
        backgroundColor:
          variantStyle.disabled?.backgroundColor ||
          variantStyle.backgroundColor ||
          "unset",
      },
    };
  }
);
